import React from 'react';
import type { ExperienceItem, Bullet } from '../../data/cv';
import { Bullets } from './Bullets';
import { CompanyMark } from './CompanyMark';
import { TechStack, InlineStack } from './TechStack';
import { periodEndpoints, splitLocation, roleDuration } from './helpers';
interface Props {
  item: ExperienceItem;
  isLast: boolean;
  /** One-page mode: trims summary/bullets/stack for a tighter footprint. */
  compact?: boolean;
}
/**
 * Editorial experience entry: a left "spine" carrying the index + year range,
 * and a right content well. On print each entry stays intact.
 */
export function ExperienceEntry({
  item,
  isLast,
  compact = false
}: Props) {
  const {
    start,
    end,
    same
  } = periodEndpoints(item.period);
  const {
    place,
    mode
  } = splitLocation(item.location);
  const duration = roleDuration(item.period);
  // In compact mode, flatten bullets (incl. subsections) and cap the count so
  // five detailed roles + the intro + tail fit within a 3-page budget.
  const compactBullets: Bullet[] = compact ? [...(item.bullets ?? []), ...(item.subsections?.flatMap(s => s.bullets) ?? [])].slice(0, 2) : [];
  return <article className="cv-entry group relative grid grid-cols-[82px_1fr] gap-6" data-id="element-107" data-mp="1bxmm5l4btdqv">
      {/* orange timeline rail running behind the company logos; the node dot
          sits on the logo, and the line connects down to the next entry.
          Hidden on the last entry so the rail stops cleanly. */}
      {!isLast && <span aria-hidden="true" className="pointer-events-none absolute left-[128px] top-[46px] bottom-0 w-[2px] -translate-x-1/2 rounded-full bg-[var(--accent)]/25" data-id="element-108" data-mp="1unvvdeyj65s2" />}
      {/* spine: month/year range (clearly stacked) + duration */}
      <div className="flex flex-col items-end pt-[2px] text-right" data-id="element-109" data-mp="23nrafuv7ikxd">
        <span className="whitespace-nowrap font-mono text-[11px] font-medium tabular-nums leading-none text-[var(--ink)]" data-id="element-110" data-mp="59zekojjelkb">
          {end}
        </span>
        {!same && <span className="mt-1 whitespace-nowrap font-mono text-[10px] tabular-nums leading-none text-[var(--ink-faint)]" data-id="element-111" data-mp="1ckx4o9gd30nn">
            {start}
          </span>}
        {duration && <span className="mt-2 rounded-full bg-[var(--accent)]/10 px-1.5 py-[1px] font-mono text-[8px] uppercase tracking-[0.1em] text-[var(--accent)]" data-id="element-112" data-mp="1llv8orqsv868">
            {duration}
          </span>}
      </div>

      {/* content */}
      <div className={isLast ? 'pb-0' : compact ? 'pb-4' : 'pb-8'} data-id="element-113" data-mp="1vizyd1jutlrs">
        {/* header: monogram + company / role, with meta on the right */}
        <div className="keep-with-next relative flex items-start gap-3.5" data-id="element-114" data-mp="25a4fa6t3bgp9">
          <span className="relative shrink-0" data-id="element-115" data-mp="259b2op7g9wz7">
            <CompanyMark company={item.company} mark={item.mark} domain={item.domain} logo={item.logo} data-id="element-116" data-mp="20ahrx4jjsk2q" />
            {/* timeline node sitting on the rail at the logo's bottom edge */}
            <span aria-hidden="true" className="absolute -bottom-[3px] left-1/2 h-[7px] w-[7px] -translate-x-1/2 rounded-full border-2 border-[var(--paper)] bg-[var(--accent)]" data-id="element-117" data-mp="299mnc1ztbxao" />
          </span>

          <div className="min-w-0 flex-1" data-id="element-118" data-mp="2jx31nnd3bkx4">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5" data-id="element-119" data-mp="2jw9sygukmlcc">
              <h3 className="font-display text-[19px] font-medium leading-tight text-[var(--ink)]" data-id="element-120" data-mp="2nx3krlb4wkai">
                {item.company}
              </h3>
              <div className="flex items-center gap-2.5" data-id="element-121" data-mp="2exq8ylzjfjmm">
                {item.industry && <span className="rounded-full bg-[var(--accent)]/10 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--accent)]" data-id="element-122" data-mp="208u3mwov8kpp">
                    {item.industry}
                  </span>}
                {mode && <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[var(--ink-faint)]" data-id="element-123" data-mp="1r8xih51oq3ze">
                    {mode}
                  </span>}
              </div>
            </div>

            <p className="mt-1 text-[12px] font-medium uppercase tracking-[0.1em] text-[var(--ink-soft)]" data-id="element-124" data-mp="1o8dk3v0yg4do">
              {item.title}
              {place && <span className="ml-2 font-normal normal-case tracking-normal text-[var(--ink-faint)]" data-id="element-125" data-mp="1efety8pt85ry">
                  · {place}
                </span>}
            </p>
          </div>
        </div>

        {/* body hangs under the header text, aligned past the monogram */}
        <div className="pl-[58px]" data-id="element-126" data-mp="10fx0hg1kk4c9">
          {/* COMPACT: only the first summary sentence + up to 3 flat bullets. */}
          {compact ? <>
              {item.summary?.[0] && <p className="cv-clamp-3 mt-1.5 max-w-[80ch] text-[11.5px] leading-relaxed text-[var(--ink-soft)]" data-id="element-127" data-mp="rhbjvqfnqze4">
                  {item.summary[0]}
                </p>}
              {compactBullets.length > 0 && <div className="mt-1.5 max-w-[80ch] gap-x-10 sm:columns-2" data-id="element-128" data-mp="2ls6t8uyo2ihy">
                  <Bullets items={compactBullets} data-id="element-129" data-mp="7m0ki0gbgwsk" />
                </div>}
              {item.stack && item.stack.length > 0 && <div className="mt-2" data-id="element-130" data-mp="hcbspz719s59">
                  <InlineStack stack={item.stack} data-id="element-131" data-mp="hdmslnxcyfr3" />
                </div>}
            </> : <>
              {item.summary && <div className="mt-3 max-w-[64ch] space-y-2" data-id="element-132" data-mp="3ktlj01fqn5o5">
                  {item.summary.map((p, i) => <p key={i} className="text-[12.5px] leading-relaxed text-[var(--ink-soft)]" data-id="element-133" data-mp="3prdbawkaotsv" data-mpi={i}>
                      {p}
                    </p>)}
                </div>}

              {item.bullets && <div className="mt-3.5 max-w-[68ch] gap-x-10 sm:columns-2" data-id="element-134" data-mp="192hjlap7mis0">
                  <Bullets items={item.bullets} data-id="element-135" data-mp="1e2cdln4a7oha" />
                </div>}

              {item.subsections && <div className="mt-4 gap-x-10 sm:columns-2" data-id="element-136" data-mp="3a4phmf7y7dc4">
                  {item.subsections.map((sub, i) => <div key={i} className="mb-4 break-inside-avoid last:mb-0" data-id="element-137" data-mp="363lzfm2fh57q" data-mpi={i}>
                      <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink)]" data-id="element-138" data-mp="3k2vhyse3wbev">
                        {sub.heading}
                      </p>
                      <Bullets items={sub.bullets} data-id="element-139" data-mp="2repuvyzkyabv" />
                    </div>)}
                </div>}

              {/* tech stack — grouped, icon-bearing, consistent across roles */}
              {item.stack && item.stack.length > 0 && <TechStack stack={item.stack} data-id="element-140" data-mp="316viyxmpo7ls" />}
            </>}
        </div>
      </div>
    </article>;
}